import { useEffect, useState } from 'react'
import { ChevronDown, Sparkles, Zap, Target, TrendingUp } from 'lucide-react'

interface HeroProps {
  onNavigate: (page: string) => void
}

// Floating animated orb component
const FloatingOrb = ({
  className,
  delay = 0,
  size = 'md'
}: {
  className?: string
  delay?: number
  size?: 'sm' | 'md' | 'lg' | 'xl'
}) => {
  const sizes = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32',
    xl: 'w-48 h-48'
  }

  return (
    <div
      className={`absolute rounded-full blur-xl animate-float opacity-40 ${sizes[size]} ${className}`}
      style={{ animationDelay: `${delay}s` }}
      aria-hidden="true"
    />
  )
}

export const Hero: React.FC<HeroProps> = ({ onNavigate }) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100)
    return () => clearTimeout(timer)
  }, [])

  // Subtle parallax effect on mouse move
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20
      const y = (e.clientY / window.innerHeight - 0.5) * 20
      setMousePos({ x, y })
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  const scrollToExpertise = () => {
    onNavigate('#expertise')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      scrollToExpertise()
    }
  }

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      aria-label="Welcome to Learning Xperience Consulting"
    >
      {/* Full-width Banner Background */}
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src="/images/knowledge-network-banner.png"
          alt=""
          className={`w-full h-full object-cover transition-opacity duration-1000 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-transparent to-white/80" />
      </div>

      {/* Animated floating orbs for depth */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ transform: `translate(${mousePos.x * 0.5}px, ${mousePos.y * 0.5}px)` }}
        aria-hidden="true"
      >
        <FloatingOrb className="bg-brand-blue/30 top-20 right-[20%]" delay={0} size="lg" />
        <FloatingOrb className="bg-brand-amber/40 top-[40%] right-[10%]" delay={1} size="md" />
        <FloatingOrb className="bg-brand-blue/20 bottom-[30%] right-[25%]" delay={2} size="xl" />
        <FloatingOrb className="bg-brand-amber/30 top-[60%] right-[5%]" delay={0.5} size="sm" />
      </div>

      {/* Animated geometric shapes */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        {/* Rotating ring */}
        <div
          className={`absolute top-[15%] right-[15%] w-32 h-32 border-2 border-brand-blue/20 rounded-full transition-all duration-1000 ${
            isLoaded ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-50 rotate-180'
          }`}
          style={{
            animation: isLoaded ? 'spin 20s linear infinite' : 'none',
            transform: `translate(${mousePos.x * -0.3}px, ${mousePos.y * -0.3}px)`
          }}
        />
        {/* Pulsing dot grid */}
        <div className="absolute top-[20%] right-[30%] grid grid-cols-3 gap-3">
          {[...Array(9)].map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full bg-brand-blue/30 transition-all duration-700 ${
                isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
              }`}
              style={{
                animationDelay: `${i * 0.1}s`,
                transitionDelay: `${0.8 + i * 0.05}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="container-wide relative z-10 py-20">
        <div className="max-w-3xl">
          {/* Badge */}
          <div
            className={`inline-block mb-6 transition-all duration-700 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand-blue/10 to-brand-amber/10 text-brand-blue text-sm font-semibold rounded-full border border-brand-blue/20 shadow-sm">
              <Sparkles size={16} className="text-brand-amber" aria-hidden="true" />
              EdTech Consulting & Learning Design
            </span>
          </div>

          {/* Heading */}
          <h1
            className={`text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-slate-900 tracking-tight mb-6 transition-all duration-700 delay-100 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Learning{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-amber">
              Xperience
            </span>
            <br />
            Consulting
          </h1>

          {/* Description */}
          <p
            className={`text-lg sm:text-xl text-slate-700 max-w-2xl mb-4 transition-all duration-700 delay-200 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Transform higher education with innovative EdTech solutions—from AI-enhanced
            learning design to strategic consulting that puts learners first.
          </p>
          <p
            className={`text-base sm:text-lg text-slate-600 max-w-2xl mb-8 transition-all duration-700 delay-300 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            We help education and corporate clients create immersive, personalized
            learning experiences that engage, inspire, and deliver measurable outcomes.
          </p>

          {/* Tagline */}
          <div
            className={`flex items-center gap-3 mb-8 transition-all duration-700 delay-[400ms] ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="h-px w-12 bg-gradient-to-r from-brand-blue to-brand-amber" />
            <span className="text-slate-500 text-sm font-medium">
              From chaos <span className="text-brand-blue font-semibold">→</span> to clarity
            </span>
          </div>

          {/* Buttons */}
          <div
            className={`flex flex-col sm:flex-row gap-4 mb-12 transition-all duration-700 delay-[500ms] ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <button
              onClick={scrollToExpertise}
              className="btn-primary text-lg px-8 py-4 group"
            >
              <Zap size={20} className="group-hover:rotate-12 transition-transform" aria-hidden="true" />
              Explore Our Expertise
            </button>
            <button
              onClick={() => onNavigate('#contact')}
              className="btn-secondary text-lg px-8 py-4 group"
            >
              Let's Connect
              <Target size={18} className="group-hover:scale-110 transition-transform" aria-hidden="true" />
            </button>
          </div>

          {/* Feature highlights */}
          <div
            className={`flex flex-wrap gap-6 transition-all duration-700 delay-[600ms] ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            {[
              { icon: <TrendingUp size={18} aria-hidden="true" />, text: 'AI-Enhanced Learning' },
              { icon: <Target size={18} aria-hidden="true" />, text: 'Strategic Consulting' },
              { icon: <Sparkles size={18} aria-hidden="true" />, text: 'Immersive Experiences' },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-slate-600 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-full border border-slate-200/50 shadow-sm hover:shadow-md hover:border-brand-blue/20 transition-all"
              >
                <span className="text-brand-blue">{item.icon}</span>
                <span className="text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 transition-all duration-700 delay-700 ${
          isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <button
          onClick={scrollToExpertise}
          onKeyDown={handleKeyDown}
          className="flex flex-col items-center gap-2 text-slate-500 hover:text-brand-blue transition-colors cursor-pointer focus:outline-none focus-visible:text-brand-blue"
          aria-label="Scroll to expertise section"
        >
          <span className="text-xs uppercase tracking-[0.2em]">Discover More</span>
          <ChevronDown size={20} className="animate-bounce" aria-hidden="true" />
        </button>
      </div>
    </section>
  )
}
